import React, { useState, useEffect } from "react";
import Layout from "./../Components/Layout/Layout";
import axios from "axios";
import toast from "react-hot-toast";
import { FaPlus, FaMinus } from "react-icons/fa6";
import Footer from "../Components/Layout/Footer";
import defaultImage from "../Assets/image_not_found.svg";
import { confirmAlert } from "react-confirm-alert";
import { useNavigate } from "react-router-dom";
import "../Styles/CartStyles.css";
import "react-confirm-alert/src/react-confirm-alert.css";

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [promoCode, setPromoCode] = useState( localStorage.getItem("promoCode") || "" );
  const [discount, setDiscount] = useState( Number(localStorage.getItem("discount")) || 0 );
  const [isPromoApplied, setIsPromoApplied] = useState( localStorage.getItem("isPromoApplied") === "true" || false );
  const [error, setError] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [quantityErrors, setQuantityErrors] = useState({});
  const navigate = useNavigate();

  const accessToken = sessionStorage.getItem("auth");

  const fetchCartItems = async () => {
    if (!accessToken) return;
    try {
      const response = await axios.get(
        "https://tarunika.backendapihub.com/cart/show",
        {
          headers: {
            "access-token": accessToken,
          },
        }
      );
      if (response.status === 200) {
        if (Array.isArray(response.data.data)) {
          setCartItems(response.data.data);
          
          if (response.data.data.length === 0) {
            removePromoCode();
          }
        } else {
          console.error("Cart items data is not an array");
          setCartItems([]);
        }
      } else {
        console.error("Failed to fetch cart items");
        setError("Failed to load cart items. Please try again later.");
      }
    } catch (error) {
      console.error("Error fetching cart items:", error);
      setError("Error fetching cart items. Please try again later.");
      setCartItems([]);
    }
  };

  useEffect(() => {
    fetchCartItems();
  }, [accessToken]);

  useEffect(() => {
    if (isPromoApplied && cartItems.length > 0) {
      const checkPromoValidity = async () => {
        const subtotal = calculateSubtotal();
        try {
          const response = await axios.post(
            "https://tarunika.backendapihub.com/cart/applyPromoCode",
            {
              promocode: promoCode,
              amount: subtotal,
            },
            {
              headers: {
                "access-token": accessToken,
              },
            }
          );

          if (response.status === 200) {
            if (response.data.status === "success") {
              const discountAmount = response.data.data[0].amount;
              const minAmount = response.data.data[0].min_amount || 0;

              if (subtotal >= minAmount && discountAmount > 0) {
                if (discount !== discountAmount) {
                  setDiscount(discountAmount);
                  localStorage.setItem("discount", discountAmount.toString());
                }
              } else {
                removePromoCode();
              }
            } else {
              removePromoCode();
            }
          } else {
            removePromoCode();
          }
        } catch (error) {
          console.error("Error validating promo code:", error);
          removePromoCode();
        }
      };
      checkPromoValidity();
    } else if (cartItems.length === 0) {
      removePromoCode();
    }
  }, [cartItems]);

  const handleQuantityChange = async (l1_id, size_id, change) => {
    const item = cartItems.find(
      (item) => item.l1_id === l1_id && item.size_id === size_id
    );

    // Prevent any actions for out-of-stock or deleted items
    if (!item || item.existing_qty === 0 || item.isDeleted === 1) {
      return;
    }

    const newQuantity = item ? item.qty + change : 0;

    if (newQuantity < 1) {
      return;
    }

    if (newQuantity > item.existing_qty) {
      setQuantityErrors((prevErrors) => ({
        ...prevErrors,
        [`${l1_id}-${size_id}`]: `Only ${item.existing_qty} items available in stock.`,
      }));
      return;
    } else {
      setQuantityErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[`${l1_id}-${size_id}`];
        return updatedErrors;
      });
    }

    try {
      const response = await axios.post(
        "https://tarunika.backendapihub.com/cart/add",
        {
          l1_id: l1_id,
          quantity: newQuantity,
          size_id: size_id,
        },
        {
          headers: {
            "access-token": accessToken,
          },
        }
      );
      if (response.status === 200) {
        setCartItems((prevItems) =>
          prevItems.map((item) =>
            item.l1_id === l1_id && item.size_id === size_id
              ? {
                  ...item,
                  qty: newQuantity,
                  amount: newQuantity * item.price,
                }
              : item
          )
        );
      } else {
        toast.error("Failed to update the quantity.");
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      toast.error("Error updating quantity. Please try again later.");
    }
  };

  const handleRemoveItem = async (id) => {
    confirmAlert({
      title: "Move from Cart",
      message: "Are you sure you want to remove this item from your cart?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const response = await axios.get(
                `https://tarunika.backendapihub.com/cart/delete/${id}`,
                {
                  headers: {
                    "access-token": accessToken,
                  },
                }
              );
              if (response.status === 200) {
                setCartItems((prevItems) =>
                  prevItems.filter((item) => item.id !== id)
                );
                toast.success("Item removed successfully");
              } else {
                console.error("Failed to remove item from cart");
                toast.error("Failed to remove item from cart");
              }
            } catch (error) {
              console.error("Error removing item from cart:", error);
              toast.error("Error removing item from cart");
            }
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    try {
      const subtotal = calculateSubtotal();
      const response = await axios.post(
        "https://tarunika.backendapihub.com/cart/applyPromoCode",
        {
          promocode: promoCode,
          amount: subtotal,
        },
        {
          headers: {
            "access-token": accessToken,
          },
        }
      );

      if (response.status === 200) {
        if (response.data.status === "success") {
          const discountAmount = response.data.data[0].amount;
          const minAmount = response.data.data[0].min_amount || 0;

          if (subtotal >= minAmount && discountAmount > 0) {
            setDiscount(discountAmount);
            setIsPromoApplied(true);
            setPromoError(response.data.message);
            localStorage.setItem("promoCode", promoCode);
            localStorage.setItem("discount", discountAmount.toString());
            localStorage.setItem("isPromoApplied", "true");
            toast.success(response.data.message);
          } else {
            removePromoCode();
            const errorMsg =
              subtotal < minAmount
                ? `Minimum cart value of ₹${minAmount} required for this promo code`
                : "This promo code is no longer valid for your cart total.";
            setPromoError(errorMsg);
            toast.error(errorMsg);
          }
        } else if (response.data.status === "failure") {
          removePromoCode();
          setPromoError(response.data.message);
          toast.error(response.data.message);
        }
      } else {
        toast.error("Failed to apply promo code.");
      }
    } catch (error) {
      console.error("Error applying promo code:", error);
      removePromoCode();
      if (error.response && error.response.data) {
        const errorMessage =
          error.response.data.message || "An error occurred.";
        setPromoError(errorMessage);
        toast.error(errorMessage);
      } else {
        setPromoError("An error occurred while applying the promo code.");
        toast.error("An error occurred while applying the promo code.");
      }
    }
  };

  const removePromoCode = () => {
    setPromoCode("");
    setDiscount(0);
    setIsPromoApplied(false);
    localStorage.removeItem("promoCode");
    localStorage.removeItem("discount");
    localStorage.removeItem("isPromoApplied");
    setPromoError("");
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((acc, item) => {
      // Exclude deleted and out-of-stock items from subtotal
      if (item.isDeleted === 1 || item.existing_qty === 0) {
        return acc;
      }
      return acc + item.amount;
    }, 0);
  };

  const calculateDiscount = () => {
    return discount;
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error("Cart is empty. Please add items to proceed.");
      return;
    }
    
    const hasDeletedItem = cartItems.some(item => item.isDeleted === 1);
    const hasOutOfStockItem = cartItems.some(item => item.existing_qty === 0);

    if (hasDeletedItem || hasOutOfStockItem) {
      const errorMessage = hasDeletedItem 
        ? "Some products are no longer available. Please remove them to complete your purchase."
        : "Some products are out of stock. Please remove them to complete your purchase.";
      
      toast.error(errorMessage);
      return;
    }
    
    navigate("/checkout", { state: { cartItems, discount } });
    window.scrollTo(0, 0);
  };

  return (
    <Layout setCartItems={setCartItems} cartItems={cartItems} isNavbar={true}>
      <div className="cart-banner d-flex justify-content-center align-items-center flex-column"></div>
      {Array.isArray(cartItems) && cartItems.length === 0 ? (
        <div className="empty-cart-container">
          <div>
            <h2>Your cart is empty</h2>
          </div>
          <div className="empty-cart-message d-flex justify-content-center align-items-center flex-column">
            <p>There is nothing in your cart. Let's add some items.</p>
          </div>
        </div>
      ) : (
        <div className="container cart-main">
          <div className="cart-head-title">
            <h1>Your Cart</h1>
          </div>
          <div className="row">
            <div className="col-md-8 order-md-1 p-0">
              <div className="cart-list-container">
                <div className="cart-list">
                  <div className="cart-list-items position-relative">
                    {cartItems.map((item) => {
                      const isInStock = item.existing_qty > 0;
                      const isDeleted = item.isDeleted === 1;
                      
                      return (
                        <div key={item.id}>
                        <div className={`cart-card ${isDeleted ? "deleted-item" : ""}`}>
                            <div className="cart-img">
                              <img
                                src={
                                  item.image
                                    ? `https://tarunika.backendapihub.com/images/${item.image}`
                                    : defaultImage
                                }
                                alt="cart-img"
                                onError={(e) => (e.target.src = defaultImage)}
                              />
                            </div>
                            <div className="cart-details">
                              <div className="cart-title-desc">
                                <h3 className="m-0">{item.product_name}</h3>
                                <div
                                  className="cart-remove-option"
                                  onClick={() =>
                                    item.id && handleRemoveItem(item.id)
                                  }
                                >
                                  Remove
                                </div>
                                <div className="cart-size">
                                  <p style={{ marginBottom: "0" }}>
                                    <strong>Size:</strong> {item.size_id}
                                  </p>
                                  <p>
                                    <strong>Color:</strong> {item.colour}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="cart-values">
                              <div className="labels-row">
                                <span>Price</span>
                                <span>Quantity</span>
                                <span>Total</span>
                              </div>
                              <div className="values-row">
                                <div className="price">
                                  <span>₹ {item.price.toFixed(2)}</span>
                                  <small className="tax-included">
                                    incl. of all taxes
                                  </small>
                                </div>
                                <div className="quantity">
                                  {isDeleted ? (
                                    <div className="quantity-disabled">
                                      <FaMinus className="disabled" />
                                      <input
                                        type="text"
                                        value={item.qty}
                                        readOnly
                                        disabled
                                      />
                                      <FaPlus className="disabled" />
                                    </div>
                                  ) : !isInStock ? (
                                    <div className="out-of-stock">
                                      <FaMinus className="disabled" />
                                      <input
                                        type="text"
                                        value={item.qty}
                                        readOnly
                                        disabled
                                      />
                                      <FaPlus className="disabled" />
                                    </div>
                                  ) : (
                                    <>
                                      <FaMinus
                                        className="minus"
                                        onClick={() =>
                                          handleQuantityChange(
                                            item.l1_id,
                                            item.size_id,
                                            -1
                                          )
                                        }
                                      />
                                      <input
                                        type="text"
                                        value={item.qty}
                                        readOnly
                                      />
                                      <FaPlus
                                        className={`plus ${
                                          item.qty >= item.existing_qty
                                            ? "disabled"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          item.qty < item.existing_qty &&
                                          handleQuantityChange(
                                            item.l1_id,
                                            item.size_id,
                                            1
                                          )
                                        }
                                      />
                                    </>
                                  )}
                                </div>
                                <div className="total">
                                  <span>₹ {item.amount.toFixed(2)}</span>
                                  <small className="tax-included">
                                    incl. of all taxes
                                  </small>
                                </div>
                              </div>
                              {isDeleted ? (
                                <p className="error-message">
                                  This product is no longer available. Please remove it from your cart.
                                </p>
                              ) : !isInStock ? (
                                <p className="error-message">
                                  This product is currently out of stock
                                </p>
                              ) : (
                                quantityErrors[`${item.l1_id}-${item.size_id}`] && (
                                  <p className="error-message">
                                    {quantityErrors[`${item.l1_id}-${item.size_id}`]}
                                  </p>
                                )
                              )}
                            </div>
                          </div>
                          <hr className="m-0 card-divider" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4 order-md-2 cart-order-summary">
              <h1 className="summary-title m-0 p-0">Order Summary</h1>
              <hr />
              <div className="calculation">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="m-0 p-0">Subtotal</h3>
                  <span className="m-0 p-0">
                    ₹ {calculateSubtotal().toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="m-0 p-0">Shipping</h3>
                  <span
                    className="m-0 p-0"
                    style={{ color: "rgba(13, 158, 0, 1)" }}
                  >
                    Free
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="m-0 p-0">Coupon Discount</h3>
                  <span className="m-0 p-0">
                    ₹ {calculateDiscount().toFixed(2)}
                  </span>
                </div>
              </div>
              <hr />
              <div className="Total d-flex justify-content-between align-items-center">
              <h3 className="m-0 p-0">Total</h3>
              <span className="m-0 p-0">₹ {calculateTotal().toFixed(2)}</span>
              </div>
              <hr />
              <div className="code-wrap">
                <div className="promocode">
                  <h3 className="m-0 p-0">Discount promocode</h3>
                  <div className="position-relative">
                    <input
                      type="text"
                      placeholder="Enter promocode"
                      disabled={isPromoApplied}
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                    />
                    {!isPromoApplied ? (
                      <button onClick={applyPromoCode}>Apply</button>
                    ) : (
                      <button
                        onClick={removePromoCode}
                        style={{ padding: "8px 26.9px 9px 26.9px" }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {promoError && (
                    <p
                      style={{
                        color: isPromoApplied ? "green" : "red",
                        fontSize: "13px",
                        marginBottom: "5px",
                      }}
                    >
                      {promoError}
                    </p>
                  )}
                </div>
              </div>
              <button className="checkout-btn m-0 p-0" onClick={handleCheckout}>
                CHECKOUT
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer showBothDivs={false} />
    </Layout>
  );
};

export default CartPage;